import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { SupportedCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import {
  BodyL,
  BodyM,
  BodyS,
  Caption,
} from '@sorare/core/src/atoms/typography';
import { CreditCardIcon } from '@sorare/core/src/components/paymentMethods/Icon/CreditCardIcon';
import { FiatWalletIcon } from '@sorare/core/src/components/paymentMethods/Icon/FiatWalletIcon';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useFiatBalance } from '@sorare/core/src/hooks/wallets/useFiatBalance';
import { invoiceTableMessages } from '@sorare/core/src/lib/glossary';
import { PaymentMethod } from '@sorare/core/src/lib/paymentMethod';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { usePaymentContext } from 'components/buyActions/Context';
import { PaymentBoxAmountWithConversion } from 'components/buyActions/PaymentBox/AmountWithConversion';

import ConversionCreditRow from './ConversionCreditRow';
import { useCalculateWalletAndCreditCardSplit } from './useCalculateWalletAndCreditCardSplit';
import { useFeesDetailsLink } from './useFeesDetailsLink';
import { useWalletProjectedBalances } from './useWalletProjectedBalances';

const FeesLabel = styled(BodyM)`
  display: flex;
  gap: var(--half-unit);
  align-items: center;
`;

const InfoCircle = styled(FontAwesomeIcon)`
  opacity: 0.5;
`;

const FeesDetails = styled.a`
  color: var(--c-neutral-600);
  text-decoration: underline;
  &:hover {
    color: var(--c-neutral-600);
  }
`;

const Table = styled.div`
  padding: 0 var(--double-unit);
  background-color: var(--c-neutral-300);
  @media ${tabletAndAbove} {
    border-radius: var(--unit);
  }
`;

export const Row = styled(SBHorizontal)`
  position: relative;
  width: 100%;
  align-items: center;
  padding: var(--intermediate-unit) 0;
  &:not(:first-child) {
    border-top: solid 1px var(--c-neutral-400);
  }
`;

export const SummaryTable = () => {
  const helpLink = useFeesDetailsLink();
  const {
    walletPreferences: { onlyShowFiatCurrency },
  } = useCurrentUserContext();
  const isTablet = useIsTabletAndAbove();
  const { fiatCurrencyForMonetaryAmount } = useFiatBalance();

  const {
    fees,
    feesMonetaryAmount,
    selectedOptionConfig,
    showConversionCredit,
    monetaryAmount: subtotalMonetaryAmount,
    totalMonetaryAmount,
    paymentCurrency,
    payWithConversionCreditsOnly,
  } = usePaymentContext();

  const { fiatWalletMonetaryAmount, creditCardMonetaryAmount } =
    useCalculateWalletAndCreditCardSplit(totalMonetaryAmount);
  const { toMonetaryAmount } = useMonetaryAmount();

  const { projectedFiatBalance, projectedEthBalance } =
    useWalletProjectedBalances(
      totalMonetaryAmount,
      selectedOptionConfig?.value
    );

  const sharedPaymentBoxAmountWithConversionProps = {
    hideExponent: onlyShowFiatCurrency,
    primaryCurrency: paymentCurrency,
    inline: true,
    reverse: true,
  };

  const showFeesRow = fees !== 0;

  const showCreditCardAndWalletSplitRow =
    !!selectedOptionConfig?.topUpWalletWithCC;

  const showSubtotalRow =
    showConversionCredit || showFeesRow || showCreditCardAndWalletSplitRow;

  return (
    <Vertical gap={2}>
      <ConditionalWrapper
        Wrapper={FullWidthContainer.NoPadding}
        wrap={!isTablet}
      >
        <Table>
          {showSubtotalRow && (
            <Row>
              <Vertical grow>
                <SBHorizontal>
                  <BodyM color="var(--c-neutral-700)">
                    <FormattedMessage {...invoiceTableMessages.subtotal} />
                  </BodyM>
                  <PaymentBoxAmountWithConversion
                    monetaryAmount={subtotalMonetaryAmount}
                    {...sharedPaymentBoxAmountWithConversionProps}
                  />
                </SBHorizontal>
                {showFeesRow && (
                  <SBHorizontal>
                    <FeesLabel color="var(--c-neutral-700)">
                      <FormattedMessage
                        id="PaymentBox.creditCardFees"
                        defaultMessage="Credit card fees"
                      />
                      <Tooltip
                        placement="top"
                        title={
                          <Caption>
                            <FormattedMessage
                              id="PaymentBox.creditCardFeesInfo"
                              defaultMessage="A {fees, number, percent} credit card fee is charged at the time of the purchase. {more}"
                              values={{
                                fees,
                                more: (
                                  <FeesDetails href={helpLink} target="_blank">
                                    <FormattedMessage
                                      id="PaymentBox.moreDetails"
                                      defaultMessage="More details"
                                    />
                                  </FeesDetails>
                                ),
                              }}
                            />
                          </Caption>
                        }
                      >
                        <InfoCircle icon={faInfoCircle} />
                      </Tooltip>
                    </FeesLabel>
                    <PaymentBoxAmountWithConversion
                      monetaryAmount={feesMonetaryAmount}
                      {...sharedPaymentBoxAmountWithConversionProps}
                    />
                  </SBHorizontal>
                )}
              </Vertical>
            </Row>
          )}
          {showConversionCredit && (
            <Row>
              <ConversionCreditRow
                paymentBoxAmountWithConversionProps={
                  sharedPaymentBoxAmountWithConversionProps
                }
              />
            </Row>
          )}
          {showCreditCardAndWalletSplitRow ? (
            <>
              <Row>
                <Horizontal>
                  <FiatWalletIcon size="xs" />
                  <BodyS color="var(--c-neutral-700)">
                    <FormattedMessage
                      id="PaymentBox.payWithCashWallet"
                      defaultMessage="Pay with Cash Wallet"
                    />
                  </BodyS>
                </Horizontal>
                <PaymentBoxAmountWithConversion
                  color="var(--c-neutral-700)"
                  monetaryAmount={fiatWalletMonetaryAmount}
                  {...sharedPaymentBoxAmountWithConversionProps}
                />
              </Row>
              <Row>
                <Horizontal>
                  <CreditCardIcon size="xs" />
                  <BodyL bold>
                    <FormattedMessage
                      id="PaymentBox.chargedToCard"
                      defaultMessage="Charged to card"
                    />
                  </BodyL>
                </Horizontal>
                <PaymentBoxAmountWithConversion
                  bold
                  monetaryAmount={creditCardMonetaryAmount}
                  {...sharedPaymentBoxAmountWithConversionProps}
                />
              </Row>
            </>
          ) : (
            <Row>
              <Horizontal>
                {selectedOptionConfig?.Icon &&
                  !payWithConversionCreditsOnly && (
                    <selectedOptionConfig.Icon size="xs" />
                  )}
                <BodyL bold color="var(--c-neutral-1000)">
                  <FormattedMessage
                    id="SummaryTableV2.total"
                    defaultMessage="Order total"
                  />
                </BodyL>
              </Horizontal>
              <PaymentBoxAmountWithConversion
                monetaryAmount={totalMonetaryAmount}
                bold
                MainAmountComponent={BodyL}
                {...sharedPaymentBoxAmountWithConversionProps}
              />
            </Row>
          )}
        </Table>
      </ConditionalWrapper>
      {selectedOptionConfig?.value === PaymentMethod.ETH_WALLET &&
        !payWithConversionCreditsOnly && (
          <Horizontal>
            <BodyS color="var(--c-neutral-600)">
              <FormattedMessage
                id="SummaryTableV2.remainingEthBalance"
                defaultMessage="ETH Wallet balance left after transaction:"
              />
            </BodyS>
            <PaymentBoxAmountWithConversion
              monetaryAmount={toMonetaryAmount({
                wei: projectedEthBalance,
                referenceCurrency: SupportedCurrency.WEI,
              })}
              color="var(--c-neutral-600)"
              MainAmountComponent={BodyS}
              hideExponent
              primaryCurrency={paymentCurrency}
              inline
            />
          </Horizontal>
        )}
      {selectedOptionConfig?.value === PaymentMethod.FIAT_WALLET && (
        <Horizontal>
          <BodyS color="var(--c-neutral-600)">
            <FormattedMessage
              id="SummaryTableV2.remainingBalance"
              defaultMessage="Cash Wallet balance left after transaction:"
            />
          </BodyS>
          <PaymentBoxAmountWithConversion
            monetaryAmount={toMonetaryAmount({
              [fiatCurrencyForMonetaryAmount]: projectedFiatBalance,
              referenceCurrency:
                fiatCurrencyForMonetaryAmount.toUpperCase() as SupportedCurrency,
            })}
            color="var(--c-neutral-600)"
            MainAmountComponent={BodyS}
            hideExponent
            primaryCurrency={paymentCurrency}
            inline
          />
        </Horizontal>
      )}
    </Vertical>
  );
};

export default SummaryTable;
